import { FormattedUsageDataPoint, TableDataResult, TableDataRow } from '../types';

/**
 * Transforms chart-formatted usage data into table-formatted data.
 *
 * Chart data is organized by time (each object is a time period with group values).
 * Table data is organized by group (each row is a group with time period values).
 * Handles duplicate time entries by summing values.
 *
 * @param formattedUsageData Chart data points with time and group values
 * @param groups List of unique group names from the chart data
 * @returns Table rows and ordered time column keys
 */
export function formatUsageDataForTable(
  formattedUsageData: FormattedUsageDataPoint[],
  groups: string[]
): TableDataResult {
  // Extract UNIQUE time columns in order (deduplicate)
  const timeColumns: string[] = [];
  const seenTimes = new Set<string>();
  formattedUsageData.forEach((d) => {
    if (!seenTimes.has(d.time)) {
      seenTimes.add(d.time);
      timeColumns.push(d.time);
    }
  });

  // Sort groups alphabetically (case-insensitive)
  const sortedGroups = [...groups].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // Pivot: for each group, create a row with SUMMED values for each time period
  const rows: TableDataRow[] = sortedGroups.map((group) => {
    const row: TableDataRow = { group };

    // Initialize all time columns to 0
    timeColumns.forEach((time) => {
      row[time] = 0;
    });

    // Sum values for each time period (handles any remaining duplicates)
    formattedUsageData.forEach((dataPoint) => {
      const value = dataPoint[group];
      if (typeof value === 'number') {
        row[dataPoint.time] = (row[dataPoint.time] as number) + value;
      }
    });

    // Format to 2 decimal places, dash for zero values (UI display)
    timeColumns.forEach((time) => {
      const value = row[time] as number;
      row[time] = value === 0 ? '-' : value.toFixed(2);
    });

    return row;
  });

  return { rows, timeColumns };
}
